import type { ReactNode } from 'react';
import type { PurchaseInvoiceDetail } from '@miraki/contracts';
import { Chip, Empty, Money, Panel, Table } from '../../ui';

/**
 * CR-56 P1 — the body of A-45 and R-27: one purchase invoice, its pieces, and what has been paid.
 *
 * ONE component for both audiences, because the figures must read the same on both sides and a
 * second table is a second place for them to drift. The two screens differ only in what they pass
 * as `labels`: A-45 is Miraki's own console and passes literals; R-27 is a trade customer's screen
 * (D47, CR-02) and passes `t()`. Nothing here is language, so nothing here is a string.
 *
 * **The reconciliation block is the point of the page.** Subtotal of the pieces, then the
 * adjustments, then the invoice total as recorded — shown even when `adjustments` is zero, and
 * never "fixed" when it is not (FN-17, D76: tax is posted separately, so pieces and total
 * usually differ). Every figure is the API's; nothing is computed here (invariant 1).
 */
export interface PurchaseInvoiceLabels {
  piecesTitle: string;
  piecesEmpty: string;
  colJob: string;
  colDescription: string;
  colValue: string;
  subtotal: string;
  adjustments: string;
  total: string;
  paymentsTitle: string;
  paymentsEmpty: string;
  colReceived: string;
  colBankReference: string;
  colAmount: string;
  paid: string;
  outstanding: string;
  /** CR-56 P4 — the retailer's claims awaiting Admin, and the rejected ones. */
  submissionsTitle: string;
  submissionsNote: string;
  colSubmitted: string;
  colStatus: string;
  colProof: string;
  viewProof: string;
  noProof: string;
  submissionStatus: Record<PurchaseInvoiceDetail['submissions'][number]['status'], string>;
  state: Record<PurchaseInvoiceDetail['state'], string>;
}

const STATE_TONE = { ISSUED: 'us', PART_PAID: 'us', PAID: 'done', CANCELLED: 'lapse' } as const;
const SUBMISSION_TONE = { PENDING: 'run', REJECTED: 'stop' } as const;

export const PurchaseInvoiceStateChip = ({ state, labels }: { state: PurchaseInvoiceDetail['state']; labels: PurchaseInvoiceLabels }) => (
  <Chip tone={STATE_TONE[state]}>{labels.state[state]}</Chip>
);

function Line({ k, children, strong }: { k: string; children: ReactNode; strong?: boolean }) {
  return (
    <div className={`mk-pi-line ${strong ? 'mk-pi-strong' : ''}`}>
      <span>{k}</span>
      <span>{children}</span>
    </div>
  );
}

type Submission = PurchaseInvoiceDetail['submissions'][number];

export function PurchaseInvoiceBody({ inv, labels, fileHref, actions }: {
  inv: PurchaseInvoiceDetail; labels: PurchaseInvoiceLabels;
  /** Where a submission's proof file is served for THIS audience. */
  fileHref?: (proofId: string) => string;
  /** A-45 only: Admin's Verify / Reject for a PENDING claim. A rejected one shows its reason instead. */
  actions?: (s: Submission) => ReactNode;
}) {
  const money = (amount: string) => <Money amount={amount} currency={inv.currency} trimZeros={false} />;
  // An API from before CR-56 P4 sends no `submissions`. The two ship together, but containers overlap
  // during a rollout, and a missing list should read as "no claims", not take the page down.
  const submissions: Submission[] = (inv as { submissions?: Submission[] }).submissions ?? [];
  return (
    <>
      <Panel title={labels.piecesTitle} pad>
        {inv.pieces.length === 0 ? (
          <Empty title={labels.piecesEmpty} body="" />
        ) : (
          <Table
            rows={inv.pieces}
            rowKey={(p) => p.pieceId}
            columns={[
              { header: labels.colJob, render: (p) => <code>{p.jobNumber}</code> },
              { header: labels.colDescription, render: (p) => p.description },
              { header: labels.colValue, numeric: true, render: (p) => money(p.wholesaleValue) },
            ]}
          />
        )}
        <Line k={labels.subtotal}>{money(inv.piecesSubtotal)}</Line>
        <Line k={labels.adjustments}>{money(inv.adjustments)}</Line>
        <Line k={labels.total} strong>{money(inv.recordedTotal)}</Line>
      </Panel>

      {submissions.length > 0 ? (
        <Panel title={labels.submissionsTitle} pad>
          {/* A claim is not a payment: it is listed here, and is NOT in Paid or Outstanding below. */}
          <p className="mk-hint">{labels.submissionsNote}</p>
          <Table
            rows={submissions}
            rowKey={(s) => s.proofId}
            columns={[
              { header: labels.colSubmitted, render: (s) => s.submittedAt.slice(0, 10) },
              { header: labels.colBankReference, render: (s) => s.bankReference ?? '—' },
              { header: labels.colAmount, numeric: true, render: (s) => money(s.amount) },
              { header: labels.colStatus, render: (s) => <Chip tone={SUBMISSION_TONE[s.status]}>{labels.submissionStatus[s.status]}</Chip> },
              {
                header: labels.colProof,
                render: (s) => (s.hasFile && fileHref
                  ? <a href={fileHref(s.proofId)} target="_blank" rel="noreferrer">{labels.viewProof}</a>
                  : <span className="mk-dim">{labels.noProof}</span>),
              },
              {
                header: '',
                render: (s) => (s.status === 'REJECTED'
                  // PY-11 — what to fix, in Admin's own words.
                  ? <span>{s.rejectionReason}</span>
                  : (actions ? actions(s) : null)),
              },
            ]}
          />
        </Panel>
      ) : null}

      <Panel title={labels.paymentsTitle} pad>
        {inv.payments.length === 0 ? (
          <Empty title={labels.paymentsEmpty} body="" />
        ) : (
          <Table
            rows={inv.payments}
            rowKey={(p) => p.paymentId}
            columns={[
              { header: labels.colReceived, render: (p) => p.receivedOn },
              { header: labels.colBankReference, render: (p) => p.bankReference ?? '—' },
              { header: labels.colAmount, numeric: true, render: (p) => money(p.amount) },
            ]}
          />
        )}
        <Line k={labels.paid}>{money(inv.paid)}</Line>
        <Line k={labels.outstanding} strong>{money(inv.outstanding)}</Line>
      </Panel>
    </>
  );
}
